import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { Box, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import type { KeyboardEvent } from 'react'

import type { CascadeMarketId, MarketSegmentData } from '@/features/bidding/types/market'

import { FONT_SIZE, MONOSPACE_FONT_FAMILY, STATUS_ICON_SIZE, WHITE_ALPHA } from './styles'

interface MarketSegmentProps {
  market: MarketSegmentData
  isSelected: boolean
  isDisabled?: boolean
  onSelect: (id: CascadeMarketId) => void
}

function MarketSegment({ market, isSelected, isDisabled = false, onSelect }: Readonly<MarketSegmentProps>) {
  const { id, label, gateDisplay, state, bidSubmitted, clearingPrice, countdown } = market

  const handleClick = () => {
    if (!isDisabled) {
      onSelect(id)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      onSelect(id)
    }
  }

  // Render status icon - uses shape to convey status (not just color)
  const renderStatusIcon = () => {
    const iconSx = { fontSize: STATUS_ICON_SIZE, color: WHITE_ALPHA.HIGH }

    if (bidSubmitted) {
      return <CheckCircleIcon sx={iconSx} />
    }
    return <RadioButtonUncheckedIcon sx={iconSx} />
  }

  // Determine status line content based on state
  const renderStatusLine = () => {
    if (state === 'open') {
      return (
        <Typography
          sx={{
            fontSize: FONT_SIZE.SMALL,
            fontFamily: MONOSPACE_FONT_FAMILY,
            color: WHITE_ALPHA.HIGH,
          }}
        >
          {countdown}
        </Typography>
      )
    }

    // Closed state
    if (bidSubmitted && clearingPrice) {
      return (
        <Typography
          sx={{
            fontSize: FONT_SIZE.SMALL,
            fontFamily: MONOSPACE_FONT_FAMILY,
            color: WHITE_ALPHA.MEDIUM,
          }}
        >
          {clearingPrice}
        </Typography>
      )
    }

    return (
      <Typography
        sx={{
          fontSize: FONT_SIZE.SMALL,
          fontFamily: MONOSPACE_FONT_FAMILY,
          color: WHITE_ALPHA.LOW,
        }}
      >
        Closed
      </Typography>
    )
  }

  // Determine opacity based on state
  const isDimmed = (state === 'closed' && !bidSubmitted) || isDisabled

  const containerSx: SxProps<Theme> = {
    px: 1.5,
    py: 0.5,
    minWidth: 85,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    position: 'relative',
    opacity: isDimmed ? 0.5 : 1,
    transition: 'background-color 100ms ease, box-shadow 100ms ease',
    // Subtle inset shadow when selected (skeuomorphic)
    boxShadow: isSelected && !isDisabled ? 'inset 0 1px 3px rgba(0, 0, 0, 0.3)' : 'none',
    bgcolor: isSelected && !isDisabled ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
    // Reset default button styles
    border: 'none',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: 'auto',
    '&:hover': {
      bgcolor: isDisabled ? 'transparent' : 'rgba(255, 255, 255, 0.05)',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: isDisabled ? 'none' : '2px solid',
      outlineColor: 'primary.light',
      outlineOffset: 2,
    },
  }

  return (
    <Box
      aria-disabled={isDisabled}
      aria-label={`${label} market, gate closes at ${gateDisplay}, ${state === 'open' ? 'gate open' : 'gate closed'}${bidSubmitted ? ', bid submitted' : ''}${isDisabled ? ', disabled' : ''}`}
      aria-pressed={isDisabled ? undefined : isSelected}
      component="button"
      disabled={isDisabled}
      sx={containerSx}
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Market label with status icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
        {renderStatusIcon()}
        <Typography
          sx={{
            fontSize: FONT_SIZE.MEDIUM,
            fontWeight: 600,
            color: isSelected ? WHITE_ALPHA.FULL : WHITE_ALPHA.HIGH,
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Gate time */}
      <Typography
        sx={{
          fontSize: FONT_SIZE.SMALL,
          fontFamily: MONOSPACE_FONT_FAMILY,
          color: WHITE_ALPHA.MEDIUM,
          mb: 0.25,
          pl: 2, // Align with label text (after icon)
        }}
      >
        {gateDisplay}
      </Typography>

      {/* Status line */}
      <Box sx={{ pl: 2 }}>{renderStatusLine()}</Box>
    </Box>
  )
}

export default MarketSegment
